import { createTheme, PaletteColorOptions } from "@mui/material/styles";
import {
  colors,
  gradients,
  semanticTokensLight,
  staticTokens,
  TColors,
  TGradients,
  TSemanticTokens,
} from "styles/colorVariables";
import { TTypography } from "./types";

type TStaticTokens = typeof staticTokens;

type Camel<S extends string> = S extends `${infer H}-${infer T}` ? `${H}${Capitalize<Camel<T>>}` : S;
type CamelKeys<T> = { [K in keyof T as Camel<K & string>]: T[K] };
type TThemeSemanticT = CamelKeys<TSemanticTokens & TStaticTokens>;

const toCamel = (key: string) => key.replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase());

const customColors = Object.keys(colors)
  .map((key) => ({ [key]: `var(--${key})` }))
  .reduce((res, current) => Object.assign(res, current), {}) as TColors;

const semanticVarMap = [...Object.keys(semanticTokensLight), ...Object.keys(staticTokens)]
  .map((key) => ({ [toCamel(key)]: `var(--${key})` }))
  .reduce((res, current) => Object.assign(res, current), {}) as TThemeSemanticT;

const gradientVarMap = Object.keys(gradients)
  .map((key) => ({ [key]: `var(--${key})` }))
  .reduce((res, current) => Object.assign(res, current), {}) as TGradients;

declare module "@mui/material/styles" {
  interface SimplePaletteColorOptions {
    hover?: string;
    iconBg?: string;
    iconBgHover?: string;
    pending?: string;
    mine?: string;
    accepted?: string;
  }
  interface PaletteOptions {
    pending?: PaletteColorOptions;
    mine?: PaletteColorOptions;
    accepted?: PaletteColorOptions;
    awaiting_proposal: PaletteColorOptions;
    awaiting_signature: PaletteColorOptions;
    contract_signed: PaletteColorOptions;
    present_proposal: PaletteColorOptions;
    take_action: PaletteColorOptions;
    request_proposal: PaletteColorOptions;
    qualify: PaletteColorOptions;
    closed: PaletteColorOptions;
    canceled: PaletteColorOptions;
    view_offer: PaletteColorOptions;
    new: PaletteColorOptions;
  }

  interface Theme {
    formComponents?: {
      typography: TTypography;
    };
    colors: TColors;
    gradients: TGradients;
    semantic: TThemeSemanticT;
  }

  interface ThemeOptions {
    formComponents?: {
      typography: TTypography;
    };
    colors: TColors;
    gradients: TGradients;
    semantic: TThemeSemanticT;
  }
}

const theme = createTheme({
  formComponents: { typography: { label: { color: customColors["--luxe-blue"], fontSize: "14px", fontWeight: 500 } } },
  colors: {
    ...colors,
  },
  gradients: { ...gradientVarMap },
  semantic: { ...semanticVarMap },
  typography: {
    allVariants: { fontFamily: "Barlow" },
  },
  palette: {
    pending: {
      main: colors["graham-grey"],
      hover: colors["institutional-grey-hover"],
    },
    mine: {
      main: colors["blue-ribbon"],
      hover: colors["mangus-blue-hover"],
    },
    accepted: {
      main: colors.eggplant,
      hover: colors["blue-purple-hover"],
    },
    awaiting_proposal: {
      main: colors["nepal-blue"],
      iconBg: colors["institutional-grey"],
    },
    awaiting_signature: {
      main: colors["move-it-forward-disabled"],
    },
    contract_signed: {
      main: colors["green-haze"],
      hover: colors["green-haze-highlighted"],
      iconBg: colors["green-haze-highlighted"],
      iconBgHover: colors["green-haze-button-highlighted"],
    },
    present_proposal: {
      main: colors["blue-ribbon"],
      hover: colors["banner-blue-highlighted"],
      iconBg: colors["banner-blue-highlighted"],
      iconBgHover: colors["mangus-blue-hover"],
    },
    take_action: {
      main: colors["blue-ribbon"],
      hover: colors["banner-blue-highlighted"],
      iconBg: colors["banner-blue-highlighted"],
      iconBgHover: colors["mangus-blue-hover"],
    },
    request_proposal: {
      main: colors["blue-ribbon"],
      hover: colors["banner-blue-highlighted"],
      iconBg: colors["banner-blue-highlighted"],
      iconBgHover: colors["mangus-blue-hover"],
    },
    qualify: {
      main: colors["blue-ribbon"],
      hover: colors["banner-blue-highlighted"],
      iconBg: colors["banner-blue-highlighted"],
      iconBgHover: colors["mangus-blue-hover"],
    },
    new: {
      main: colors["blue-ribbon"],
      hover: colors["banner-blue-highlighted"],
      iconBg: colors["banner-blue-highlighted"],
      iconBgHover: colors["mangus-blue-hover"],
    },
    closed: {
      main: colors["green-haze"],
      hover: colors["green-haze-highlighted"],
      iconBg: colors["green-haze-highlighted"],
      iconBgHover: colors["green-haze-button-highlighted"],
    },
    canceled: {
      main: colors["move-it-forward-disabled"],
    },
    view_offer: {
      main: colors["status-alert-fill"],
    },
  },
});

export { theme };
